// Library Methods
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCallback, useMemo, useState } from "react";
import { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";

// Components
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ExamCardMenu from "../ExamCardMenu/ExamCardMenu";
import ExamSlider from "src/components/containers/shared/ExamSlider/ExamSlider";
import SharedModal from "src/components/shared/modals/SharedModal/SharedModal";
import {
  CopyIcon,
  DownloadIcon,
  EditIcon,
  EllipsisIcon,
  UserEditIcon,
  ViewFileIcon,
  HeartbeatIcon,
} from "src/assets/icons";

// Styles
import { noHoverBackgroundSx } from "src/styles/mui/muiStylesHelper";

// Types
import { ExamType } from "src/services/exams/types";
import { PatientType } from "src/services/patients/types";

// Hooks
import useExamCardHelpers from "src/hooks/exams/useExamCardHelpers";
import useOnDemandReportGeneration from "src/hooks/reports/useOnDemandReportGeneration";
import { useDuplicateExams } from "src/hooks/exams/useDuplicateExams";

// Utils
import { getTruncatedText } from "src/utils/text/textHelper";

// Contexts
import { useExamSliderContext } from "src/contexts/exams/ExamSliderContext";
import { useExamTransferContext } from "src/contexts/exams/ExamTransferContext";
import { useLiveUpdateContext } from "src/contexts/live-update/LiveUpdateContext";
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Constants
import { DEFAULT_REPORT_TYPE } from "src/constants/enums";

type ExamCardButtonsType = {
  exam: ExamType;
  selectedPatient: PatientType;
  refetchExams: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<ExamType[], Error>>;
};

const ExamCardButtons = ({
  exam,
  selectedPatient,
  refetchExams,
}: ExamCardButtonsType) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clinicInfo } = useClinicContext();
  const { duplicateExam } = useDuplicateExams();

  const { actions } = useLiveUpdateContext();
  const { handleViewLiveUpdateClick } = actions;

  const { downloadFile } = useOnDemandReportGeneration({
    patientId: selectedPatient?.id,
    examId: exam?.id,
  });

  const fileType = useMemo(
    () => clinicInfo?.settings?.defaultReportType ?? DEFAULT_REPORT_TYPE,
    [clinicInfo?.settings?.defaultReportType]
  );

  const patientId = useMemo(() => selectedPatient.id, [selectedPatient.id]);

  const [isDownloading, setIsDownloading] = useState(false);

  const [openEditExamSlider, setOpenEditExamSlider] = useState(false);
  const [isLoadingSlider, setIsLoadingSlider] = useState(false);

  const [isDuplicating, setIsDuplicating] = useState(false);
  const [openDuplicateModal, setOpenDuplicateModal] = useState(false);
  const [examToDuplicate, setExamToDuplicate] = useState<
    ExamType | undefined
  >();

  const { actions: transferActions } = useExamTransferContext();
  const { handleTransferClick } = transferActions;

  const { actions: sliderActions } = useExamSliderContext();
  const { editExamWrapper, handleInitializeEdit, resetValues } = sliderActions;

  const handleDuplicateClick = useCallback(
    (selectedExam: ExamType) => {
      setExamToDuplicate(selectedExam);
      setOpenDuplicateModal(true);
    },
    [setOpenDuplicateModal, setExamToDuplicate]
  );

  const handleDuplicateClose = useCallback(() => {
    setOpenDuplicateModal(false);
    setExamToDuplicate(undefined);
  }, [setOpenDuplicateModal, setExamToDuplicate]);

  const duplicateExamWrapper = useCallback(
    async (patientId: string) => {
      if (!examToDuplicate) return;

      await duplicateExam({
        patientId,
        examToDuplicate,
      });
    },
    [duplicateExam, examToDuplicate]
  );

  const handleEditClick = useCallback(
    async (selectedExam: ExamType) => {
      resetValues();
      handleInitializeEdit(selectedExam);
      setOpenEditExamSlider(true);
    },
    [resetValues, handleInitializeEdit]
  );

  const [examMenu, setExamMenu] = useState<HTMLButtonElement | null>(null);

  // menu click handler
  const examMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setExamMenu(event.currentTarget);
  };

  // menu close handler
  const examMenuClose = () => {
    setExamMenu(null);
  };

  const {
    deviceToUse,
    examToUse,
    examStatus,
    isLiveUpdateDisabled,
    isEditDisabled,
    isViewReportDisabled,
  } = useExamCardHelpers({ exam });

  const displayLiveUpdateButton = useMemo(
    () =>
      examStatus?.status !== t("Completed") &&
      examStatus?.status !== t("Terminated") &&
      examStatus?.status !== t("Invalid"),
    [t, examStatus?.status]
  );

  // buttons are ordered in terms of importance / usage
  const examMenuButtons = useMemo(() => {
    const allButtons = [
      {
        icon: <DownloadIcon />,
        tooltipText: t(`Download {{ fileType }} Report`, { fileType }),
        text: t(`Download {{ fileType }} Report`, { fileType }),
        onClick: async () => {
          setIsDownloading(true);
          await downloadFile();
          setIsDownloading(false);
        },
        display: !isViewReportDisabled,
        disabled: isDownloading,
        href: undefined,
      },
      {
        icon: <ViewFileIcon />,
        tooltipText: t("View Results"),
        text: t("View Report"),
        onClick: () => {},
        display: !isViewReportDisabled,
        disabled: false,
        href: `/patients/${patientId}/exam/${examToUse?.id}`,
      },
      {
        icon: <EditIcon />,
        text: t("Edit"),
        tooltipText: t("Edit Exam"),
        onClick: () => handleEditClick(examToUse),
        display: !isEditDisabled,
        disabled: false,
        href: undefined,
      },
      {
        icon: <CopyIcon />,
        text: t("Duplicate"),
        tooltipText: t("Duplicate Exam"),
        onClick: () => handleDuplicateClick(examToUse),
        display: true,
        disabled: false,
        href: undefined,
      },
      {
        icon: <UserEditIcon />,
        text: t("Reassign Exam"),
        tooltipText: t("Reassign Exam to Another Patient"),
        onClick: () => handleTransferClick(examToUse.id),
        display: !isViewReportDisabled,
        disabled: false,
        href: undefined,
      },
    ];

    return allButtons?.filter((examButton) => examButton?.display);
  }, [
    t,
    examToUse,
    fileType,
    patientId,
    isEditDisabled,
    isViewReportDisabled,
    isDownloading,
    downloadFile,
    handleDuplicateClick,
    handleEditClick,
    handleTransferClick,
  ]);

  // only display 3 icons total on the ExamCard component
  // if there are over 3 icons, display the ellipsis dropdown
  const displayEllipsisIcon = useMemo(() => {
    const visibleLiveUpdateIcon = !displayLiveUpdateButton ? 0 : 1;

    const otherVisibleIcons = examMenuButtons.filter(
      (examButton) => examButton.display
    )?.length;

    const totalIcons = visibleLiveUpdateIcon + otherVisibleIcons;
    return totalIcons > 3;
  }, [examMenuButtons, displayLiveUpdateButton]);

  // slice the icon array so that:
  // 3 unique icons can be displayed on the ExamCard
  // if there are over 3 icons, display the remainder icons in the ellipsis dropdown
  const iconIndexToSlice = useMemo(() => {
    const showEllipsisAndLiveUpdate =
      displayEllipsisIcon && displayLiveUpdateButton;
    const showEllipsisOnly = displayEllipsisIcon && !displayLiveUpdateButton;

    return showEllipsisAndLiveUpdate
      ? 1
      : showEllipsisOnly
        ? 2
        : examMenuButtons?.length;
  }, [displayEllipsisIcon, displayLiveUpdateButton, examMenuButtons]);

  const confirmDuplicateExam = useCallback(async () => {
    setIsDuplicating(true);

    try {
      await duplicateExamWrapper(patientId);
      await refetchExams();
      handleDuplicateClose();
    } catch (error) {
      // do nothing
    } finally {
      setIsDuplicating(false);
    }
  }, [duplicateExamWrapper, handleDuplicateClose, refetchExams, patientId]);

  const confirmEditExam = useCallback(async () => {
    setIsLoadingSlider(true);

    try {
      await editExamWrapper(selectedPatient.id);
      await refetchExams();
      setOpenEditExamSlider(false);
    } catch (error) {
      // do nothing
    } finally {
      setIsLoadingSlider(false);
    }
  }, [editExamWrapper, refetchExams, selectedPatient.id]);

  return (
    <>
      <Tooltip title={t("Live Update Panel")}>
        <span>
          <IconButton
            disableRipple
            disableFocusRipple
            disableTouchRipple
            aria-label="live-update-icon-button"
            sx={{
              py: 0,
              height: "24px",
              color: "inherit",
              "&.Mui-disabled": {
                color: "inherit",
                opacity: "0.5",
              },
              display: displayLiveUpdateButton ? "block" : "none",
              "@keyframes fade-in-out": {
                "0%": {
                  opacity: "0.3",
                },
                "50%": {
                  opacity: "1",
                },
                "100%": {
                  opacity: "0.3",
                },
              },
              animation: isLiveUpdateDisabled
                ? "none"
                : "fade-in-out 2s infinite",
            }}
            disabled={isLiveUpdateDisabled}
            onClick={() => {
              if (isLiveUpdateDisabled) return;
              if (deviceToUse) handleViewLiveUpdateClick(deviceToUse?.id);
            }}
          >
            <HeartbeatIcon />
          </IconButton>
        </span>
      </Tooltip>

      {examMenuButtons?.slice(0, iconIndexToSlice)?.map((examButton) => (
        <Tooltip key={examButton.tooltipText} title={examButton.tooltipText}>
          <span>
            <a
              href={examButton.href}
              style={{
                textDecoration: "none",
                color: "inherit",
              }}
              onClick={(e) => {
                e.preventDefault();

                // open report in new tab if the meta key or ctrl key is pressed
                if (examButton.href && (e.metaKey || e.ctrlKey))
                  window.open(examButton.href, "_blank");
                // else open the report in the same tab
                else if (examButton.href) navigate(examButton.href);
                else examButton.onClick();
              }}
            >
              <IconButton
                sx={{
                  py: 0,
                  height: "24px",
                  color: "inherit",
                  "&.Mui-disabled": {
                    color: "inherit",
                    opacity: "0.5",
                  },
                  ...noHoverBackgroundSx,
                  display: !examButton.display ? "none" : "block",
                }}
                disabled={examButton.disabled}
              >
                {examButton.icon}
              </IconButton>
            </a>
          </span>
        </Tooltip>
      ))}

      {displayEllipsisIcon && (
        <Tooltip title={t("More Actions")}>
          <span>
            <IconButton
              disableRipple
              disableFocusRipple
              disableTouchRipple
              sx={{
                height: "24px",
                color: "inherit",
              }}
              onClick={examMenuClick}
            >
              <EllipsisIcon />
            </IconButton>
          </span>
        </Tooltip>
      )}

      <ExamCardMenu
        examMenu={examMenu}
        examMenuClose={examMenuClose}
        examMenuButtons={examMenuButtons?.slice(
          iconIndexToSlice,
          examMenuButtons?.length
        )}
      />

      {/* Slider to edit an exam */}
      <ExamSlider
        key={`edit-exam-slider-${examToUse.id}`}
        title={`${t("Edit Exam")} / ${getTruncatedText(`${selectedPatient.firstName} ${selectedPatient.lastName}`)} / ${selectedPatient?.dateOfBirth?.slice(0, 10)}`}
        isOpen={openEditExamSlider}
        isLoading={isLoadingSlider}
        onClose={() => setOpenEditExamSlider(false)}
        onConfirm={confirmEditExam}
      />

      {/* Modal to duplicate exams */}
      <SharedModal
        title={t("Duplicate Exam")}
        open={openDuplicateModal}
        modalDescription={t(
          "Are you sure you want to duplicate the selected exam?"
        )}
        confirmButtonText={t("Duplicate")}
        isDisabledButton={isDuplicating}
        onClose={handleDuplicateClose}
        onConfirm={confirmDuplicateExam}
      />
    </>
  );
};

export default ExamCardButtons;
